import { NgComponentOutlet } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
  Type,
} from '@angular/core';

import { ElementSettingsTypeTable } from '@hyper-inferno/survey-elements-core';
import { SupabaseClientService } from '../services/supabase-client/supabase-client.service';
import { SurveyBuilderService } from '../survey-builder/survey-builder.service';
import { lastValueFrom } from 'rxjs';
import { DataStoreService } from '../data-store-service/data-store.service';

const componentMap: Record<string, () => Promise<any>> = {
  'open-ended': () =>
    import('@hyper-inferno/open-ended-settings').then(
      (m) => m.OpenEndedSettings,
    ),
  'single-choice': () =>
    import('@hyper-inferno/single-choice-settings').then(
      (m) => m.SingleChoiceSettings,
    ),
  'multiple-choice': () =>
    import('@hyper-inferno/multiple-choice-settings').then(
      (m) => m.MultipleChoiceSettings,
    ),
  'click-to-rank': () =>
    import('@hyper-inferno/click-to-rank-settings').then(
      (m) => m.ClickToRankSettings,
    ),
  'drop-down-single-choice': () =>
    import('@hyper-inferno/drop-down-single-choice-settings').then(
      (m) => m.DropDownSingleChoiceSettings,
    ),
  'drop-down-multiple-choice': () =>
    import('@hyper-inferno/drop-down-multiple-choice-settings').then(
      (m) => m.DropDownMultipleChoiceSettings,
    ),
  numeric: () =>
    import('@hyper-inferno/numeric-settings').then((m) => m.NumericSettings),
  image: () =>
    import('@hyper-inferno/image-settings').then((m) => m.ImageSettings),
  'skip-logic': () =>
    import('@hyper-inferno/skip-logic-settings').then(
      (m) => m.SkipLogicSettings,
    ),
  voice: () =>
    import('@hyper-inferno/voice-settings').then((m) => m.VoiceSettings),
  video: () =>
    import('@hyper-inferno/video-settings').then((m) => m.VideoSettings),
  'single-grid': () =>
    import('@hyper-inferno/grid-single-choice-settings').then(
      (m) => m.GridSingleChoiceSettings,
    ),
  'multiple-grid': () =>
    import('@hyper-inferno/grid-multiple-choice-settings').then(
      (m) => m.GridMultipleChoiceSettings,
    ),
  display: () =>
    import('@hyper-inferno/display-settings').then((m) => m.DisplaySettings),
  location: () =>
    import('@hyper-inferno/location-settings').then((m) => m.LocationSettings),
  'mixed-grid': () =>
    import('@hyper-inferno/grid-mixed-settings').then(
      (m) => m.GridMixedSettings,
    ),
};

// The Data Flow:
// 1. User selects element (ID: 214, type: "open-ended")
//    ↓
// 2. ElementTypeSettings fetches settings:
//    SELECT * FROM es_open_ended WHERE element_id = 214
//    ↓
// 3. Gets settings record: {id: "ea3af0b6...", element_id: 214, ...}
//    ↓
// 4. Loads OpenEndedElementSettingsComponent with:
//    - elementId = 214
//    - entityId = "ea3af0b6..."
//    ↓
// 5. AutoSaveFormBase fetches form data:
//    SELECT * FROM es_open_ended WHERE id = "ea3af0b6..."
//    ↓
// 6. User edits form → auto-saves to es_open_ended using UUID

@Component({
  selector: 'app-element-type-settings',
  imports: [NgComponentOutlet],
  templateUrl: './element-type-settings.html',
  styleUrl: './element-type-settings.scss',
})
export class ElementTypeSettings {
  private surveyBuilderService = inject(SurveyBuilderService);
  private dataStoreService = inject(DataStoreService);
  surveyId = this.dataStoreService.surveyId();

  // Use a signal or simple variable for the component type

  // This signal holds the dynamic component type (or null while loading)
  loadedComponent = signal<Type<any> | null>(null);

  elementId = input.required<number>();
  elementType = input.required<string>();
  entityId = signal<string | null | undefined>(null);
  protected supabase = inject(SupabaseClientService);
  loading = signal(false);
  dynamicInputs = computed(() => ({
    entityId: this.entityId(),
    // add other shared inputs here
  }));

  // constructor() {
  //   effect(async () => {
  //     const question_type = this.elementType();
  //     if (question_type && this.elementId()) {
  //       // Map the question_type to the actual table name
  //       const tableName =
  //         ElementSettingsTypeTable[
  //           question_type as keyof typeof ElementSettingsTypeTable
  //         ];
  //       if (!tableName) {
  //         console.error(
  //           `No table mapping found for element type: ${question_type}`,
  //         );
  //         this.entityId.set(undefined);
  //         return;
  //       }

  //       // Fetch from Supabase using the mapped table name
  //       const { data, error } = await this.supabase.client
  //         .from(tableName)
  //         .select('*')
  //         .eq('element_id', this.elementId())
  //         .maybeSingle();

  //       console.log(data);

  //       if (error) {
  //         console.error('Error fetching data:', error);
  //         this.entityId.set(undefined);
  //       } else if (data) {
  //         this.entityId.set(data.id);
  //       } else {
  //         this.entityId.set(undefined);
  //       }
  //     } else {
  //       this.entityId.set(undefined);
  //     }
  //   });

  //   effect(async () => {
  //     const loader = componentMap[this.elementType()];
  //     if (loader) {
  //       try {
  //         const component = await loader();
  //         this.loadedComponent.set(component);
  //       } catch (err) {
  //         this.loadedComponent.set(null);
  //       }
  //     }
  //   });
  // }

  constructor() {
    // --- Effect 1: Data Fetching ---
    effect(async (onCleanup) => {
      const question_type = this.elementType();
      const element_id = this.elementId();
      this.loading.set(true);
      // 1. Create a "live" flag for this specific execution
      let isCurrent = true;

      // 2. Register the cleanup: If the effect triggers again,
      // we flip the flag for the OLD execution to false.
      onCleanup(() => {
        isCurrent = false;
      });

      if (question_type && element_id) {
        const tableName =
          ElementSettingsTypeTable[
            question_type as keyof typeof ElementSettingsTypeTable
          ];

        if (!tableName) {
          console.error(`No mapping for: ${question_type}`);
          this.entityId.set(undefined);
          this.loading.set(false);
          return;
        }

        try {
          const { data, error } = await this.supabase.client
            .from(tableName)
            .select('*')
            .eq('element_id', element_id)
            .limit(1)
            .maybeSingle();

          // 3. CRITICAL CHECK: Only update signal if this
          // specific execution is still the "current" one.
          if (!isCurrent) return;

          if (error || !data) {
            if (error) console.error('Error fetching data:', error);
            this.entityId.set(undefined);
          } else {
            this.entityId.set(data.id);
          }
        } catch (err) {
          if (isCurrent) this.entityId.set(undefined);
        } finally {
          if (isCurrent) {
            this.loading.set(false);
          }
        }
      } else {
        this.entityId.set(undefined);
      }
    });

    // --- Effect 2: Component Loading ---
    effect(async (onCleanup) => {
      const type = this.elementType();
      let isCurrent = true;

      onCleanup(() => {
        isCurrent = false;
      });

      const loader = componentMap[type];
      if (loader) {
        try {
          const component = await loader();
          // Check if user has already moved to a different type
          if (isCurrent) {
            this.loadedComponent.set(component);
          }
        } catch (err) {
          if (isCurrent) this.loadedComponent.set(null);
        }
      } else {
        this.loadedComponent.set(null);
      }
    });
  }

  async fixElement() {
    try {
      const payload = {
        element_id: this.elementId(),
        survey_id: this.surveyId as number,
      };
      const result = await lastValueFrom(
        this.surveyBuilderService.fixElementSettings(payload),
      );

      if (result?.success) {
        // Refresh the entity ID to show the new settings
        await this.refreshEntityId();
      }

      return result;
    } catch (error) {
      console.error('Error fixing settings:', error);
    }
  }

  private async refreshEntityId() {
    this.loading.set(true);
    const question_type = this.elementType();
    const element_id = this.elementId();

    if (!question_type || !element_id) {
      this.entityId.set(undefined);
      this.loading.set(false);
      return;
    }

    const tableName =
      ElementSettingsTypeTable[
        question_type as keyof typeof ElementSettingsTypeTable
      ];

    if (!tableName) {
      console.error(`No mapping for: ${question_type}`);
      this.entityId.set(undefined);
      this.loading.set(false);
      return;
    }

    try {
      // this.loading=true
      const { data, error } = await this.supabase.client
        .from(tableName)
        .select('*')
        .eq('element_id', element_id)
        .maybeSingle();

      if (error || !data) {
        if (error) console.error('Error fetching data:', error);
        this.entityId.set(undefined);
      } else {
        this.entityId.set(data.id);
      }
    } catch (err) {
      console.error('Error refreshing entity ID:', err);
      this.entityId.set(undefined);
    } finally {
      this.loading.set(false);
    }
  }
}
